/**
 * The scanner's palette: the things the 3D room builder can place, and the
 * sizes each one comes in.
 *
 * A piece is placed *against a surface*, so its orientation is decided by what
 * the player is looking at (the floor, a wall, the ceiling) plus one quarter
 * turn that swaps the two axes lying in that surface. The level format stores
 * axis-aligned boxes, so that swap is the whole of the orientation control —
 * and it covers everything axis-aligned boxes can express.
 */

export type BoxPieceKind = 'crate' | 'console' | 'pillar' | 'barrier';
export type BuildPieceKind = BoxPieceKind | 'drone' | 'waypoint';

/** The marker size of a route waypoint, metres. */
export const WAYPOINT_SIZE = 0.45;

/** Size variants per box piece, in metres, before the quarter turn. */
export const BOX_VARIANTS: Record<BoxPieceKind, Array<[number, number, number]>> = {
  crate: [
    [1.2, 1.2, 1.2],
    [1.6, 1.6, 1.6],
    [0.9, 0.9, 0.9],
  ],
  console: [
    [2.2, 1, 0.9],
    [3, 1.1, 0.9],
    [2.2, 1, 1.8],
  ],
  pillar: [
    [1.1, 4, 1.1],
    [1.6, 5, 1.6],
    [0.9, 2.4, 0.9],
  ],
  barrier: [
    [2.4, 1.2, 0.4],
    [4, 1.4, 0.4],
    [2.4, 2.4, 0.4],
  ],
};

export const BOX_PIECE_KINDS: readonly BoxPieceKind[] = ['crate', 'console', 'pillar', 'barrier'];
export const BUILD_PIECE_KINDS: readonly BuildPieceKind[] = [
  ...BOX_PIECE_KINDS,
  'drone',
  'waypoint',
];

export function isBoxPiece(kind: BuildPieceKind): kind is BoxPieceKind {
  return kind !== 'drone' && kind !== 'waypoint';
}

export function variantCount(kind: BuildPieceKind): number {
  return isBoxPiece(kind) ? BOX_VARIANTS[kind].length : 1;
}

/** The piece's size, with the quarter turn applied. */
export function pieceSize(
  kind: BuildPieceKind,
  variant: number,
  rotated: boolean,
  normal: [number, number, number],
): [number, number, number] {
  if (kind === 'waypoint') return [WAYPOINT_SIZE, WAYPOINT_SIZE, WAYPOINT_SIZE];
  if (!isBoxPiece(kind)) return [0.9, 0.9, 0.9];
  const variants = BOX_VARIANTS[kind];
  const base = variants[Math.min(variant, variants.length - 1)] ?? variants[0]!;
  if (!rotated) return [base[0], base[1], base[2]];
  // Swap the two axes that lie in the mounting surface: the one the normal
  // points along stays where it is.
  const axis = dominantAxis(normal);
  if (axis === 'y') return [base[2], base[1], base[0]];
  if (axis === 'x') return [base[0], base[2], base[1]];
  return [base[1], base[0], base[2]];
}

export function dominantAxis(normal: [number, number, number]): 'x' | 'y' | 'z' {
  const [x, y, z] = normal.map(Math.abs) as [number, number, number];
  if (y >= x && y >= z) return 'y';
  if (x >= z) return 'x';
  return 'z';
}
